import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ProfileEditionComponent } from '@connect-services/client/components/profile-edition/profile-edition.component';
import { ProfileInformationComponent } from '@connect-services/client/components/profile-information/profile-information.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { TranslateService, TranslationLang } from '@connect-services/client/services/translate.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { SecurityService as ApiSecurityService, UpdateMeDto } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FileTypeEnum, FormGroupType } from '@shared/client/models/form.model';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export interface SelfProfileFormType extends FormGroupType {
    address: FormControl<string | null>;
    familyName: FormControl<string>;
    givenName: FormControl<string>;
    image: FormControl<string | null>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComplexModalTemplate, ProfileEditionComponent, ProfileInformationComponent, RessourcesModule],
    selector: 'app-profile-modal',
    templateUrl: './profile-modal.component.html',
})
export class ProfileModalComponent implements OnInit {
    public readonly isEditMode = signal(false);
    public readonly isSubmitting = signal(false);
    public profileForm?: FormGroup<SelfProfileFormType> | undefined;
    public readonly selectedLang = signal<TranslationLang>(TranslationLang.FR);

    protected dialogRef = inject(MatDialogRef<ProfileModalComponent>);
    protected readonly securityService = inject(SecurityService);

    private readonly apiSecurityService = inject(ApiSecurityService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translateService = inject(TranslateService);

    public cancelEdit(): void {
        this.isEditMode.set(false);
        this.profileForm = undefined;
        this.setDefaultSelectedLang();
    }

    public async closeModal(modalResult: ModalResultEnum): Promise<void> {
        if (modalResult === ModalResultEnum.CONFIRM) {
            await this.submitForm();
            this.translateService.updateLang(this.selectedLang());

            this.cancelEdit();

            return;
        }

        this.dialogRef.close(modalResult);
    }

    public ngOnInit(): void {
        this.setDefaultSelectedLang();
    }

    public async submitForm(): Promise<void> {
        if (this.profileForm?.invalid || !this.profileForm?.dirty) {
            return;
        }

        const profileFormControls = this.profileForm.controls;

        const meDto: UpdateMeDto = {
            ...(profileFormControls.address.dirty && {
                address: profileFormControls.address.value ?? null,
            }),
            ...(profileFormControls.familyName.dirty && {
                familyName: profileFormControls.familyName.value,
            }),
            ...(profileFormControls.givenName.dirty && {
                givenName: profileFormControls.givenName.value,
            }),
            ...(profileFormControls.image.dirty && {
                image: profileFormControls.image.value,
            }),
        };

        this.isSubmitting.set(true);
        await firstValueFrom(
            this.apiSecurityService.securityControllerPatchMe({
                updateMeDto: meDto,
            }),
        );

        await this.securityService.updateMe();

        this.snackBarService.openSuccessSnackBar('profileUpdatedSuccess');
        this.isSubmitting.set(false);

        this.isEditMode.set(false);
    }

    public toggleEditMode(): void {
        this.isEditMode.set(true);

        this.profileForm = this.initForm();
    }

    private initForm(): FormGroup<SelfProfileFormType> {
        const initialAvatar = this.securityService.meSignal()!.image;

        return new FormGroup<SelfProfileFormType>({
            address: new FormControl<string | null>(this.securityService.meSignal()!.address, {
                nonNullable: false,
                validators: Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH),
            }),
            familyName: new FormControl<string>(this.securityService.meSignal()!.familyName, {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH)],
            }),
            givenName: new FormControl<string>(this.securityService.meSignal()!.givenName, {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH)],
            }),
            image: new FormControl<string | null>(initialAvatar, {
                validators: [
                    ValidatorsService.supportedFile(FormService.acceptedImageExtensions),
                    ValidatorsService.maxFileSize(FileTypeEnum.IMAGE),
                ],
            }),
        });
    }

    private setDefaultSelectedLang(): void {
        const lang = this.translateService.getCurrentLang();

        AssertService.isEnum<TranslationLang>(lang, TranslationLang);

        this.selectedLang.set(lang);
    }
}
